import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Container, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Switch, IconButton, Fab, Alert,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Button, Checkbox, FormControlLabel, 
  alpha, TableSortLabel, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { WTooltip } from '../components/ui/WTooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';

const Employees = () => {
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isNewUserAdmin, setIsNewUserAdmin] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [addError, setAddError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [revokedExpanded, setRevokedExpanded] = useState(false);

  const { activeCompany, currentUser } = useContext(AuthContext);

  const fetchUsers = async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/company/${activeCompany.id}/users`);
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des employés.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!activeCompany) return;
      setLoading(true);
      await Promise.all([
        fetchUsers(),
        axiosInstance.get(`/task/${activeCompany.id}`).then(res => setTasks(res.data)).catch(err => console.error("Failed to fetch tasks", err))
      ]);
      setLoading(false);
    };
    fetchAllData();
  }, [activeCompany]);

  const sortedUsers = useMemo(() => {
    const filteredUsers = users.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const email = user.email.toLowerCase();
      const search = searchQuery.toLowerCase();
      return fullName.includes(search) || email.includes(search);
    });

    if (filteredUsers.length === 0) return { admins: [], employees: [] };

    const activeUsers = filteredUsers.filter(u => u.CompanyUser.status === 'active');
    const admins = activeUsers.filter(u => u.CompanyUser.isAdmin);
    const employees = activeUsers.filter(u => !u.CompanyUser.isAdmin);

    const sortable = (items) => {
      items.sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === 'name') {
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().trim();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase().trim();
        } else if (sortConfig.key === 'email') {
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
        } else { // phone
          aValue = a.phone || '';
          bValue = b.phone || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    };

    sortable(admins);
    sortable(employees);

    return { admins, employees, revoked: filteredUsers.filter(u => u.CompanyUser.status === 'revoked') };
  }, [users, sortConfig, searchQuery]);

  const requestSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === 'asc';
    setSortConfig({ key, direction: isAsc ? 'desc' : 'asc' });
  };

  const handleAdminChange = async (user, newIsAdmin) => {
    setNotification({ ...notification, open: false });
    try {
      await axiosInstance.put(`/company/${activeCompany.id}/users/${user.id}`, { isAdmin: newIsAdmin, status: 'active' });
      setUsers(users.map(u => u.id === user.id ? { ...u, CompanyUser: { ...u.CompanyUser, isAdmin: newIsAdmin, status: 'active' } } : u));
      setNotification({ open: true, message: 'Rôle mis à jour.', severity: 'success' });
    } catch (err) {
      console.error('Failed to update user role', err);
      setNotification({ open: true, message: 'Erreur lors de la mise à jour du rôle.', severity: 'error' });
    }
  };

  const handleRemoveUser = async (userId) => {
    setNotification({ ...notification, open: false });
    if (window.confirm("Êtes-vous sûr de vouloir révoquer l'accès de cet employé ? Il ne pourra plus se connecter à cette entreprise.")) {
      try {
        await axiosInstance.delete(`/company/${activeCompany.id}/users/${userId}`);
        // Mettre à jour le statut localement au lieu de filtrer
        setUsers(users.map(u => u.id === userId ? { ...u, CompanyUser: { ...u.CompanyUser, status: 'revoked' } } : u));
        setNotification({ open: true, message: 'Accès révoqué avec succès.', severity: 'success' });
      } catch (err) {
        console.error('Failed to remove user', err);
        const message = err.response?.data?.message || "Erreur lors de la révocation de l'accès.";
        setNotification({ open: true, message, severity: 'error' });
      }
    }
  };

  const handlePermanentDeleteUser = async (userId) => {
    setNotification({ ...notification, open: false });
    if (window.confirm("ATTENTION : Cette action est irréversible. L'utilisateur sera définitivement supprimé de cette entreprise. Voulez-vous continuer ?")) {
      try {
        await axiosInstance.delete(`/company/${activeCompany.id}/users/${userId}/permanent`);
        setUsers(users.filter(u => u.id !== userId));
        setNotification({ open: true, message: 'Utilisateur supprimé définitivement.', severity: 'success' });
      } catch (err) {
        console.error('Failed to permanently delete user', err);
        setNotification({ open: true, message: 'Erreur lors de la suppression définitive.', severity: 'error' });
      }
    }
  };

  const handleResendInvitation = async (userId) => {
    setNotification({ ...notification, open: false });
    try {
      await axiosInstance.post('/company/resend-invitation', { userId, companyId: activeCompany.id });
      setNotification({ open: true, message: 'Invitation renvoyée avec succès !', severity: 'success' });
    } catch (err) {
      console.error('Failed to resend invitation', err);
      const message = err.response?.data?.message || "Erreur lors du renvoi de l'invitation.";
      setNotification({ open: true, message: message, severity: 'error' });
    }
  };

  const handleOpenAddDialog = () => {
    setNotification({ ...notification, open: false });
    setNewUserEmail('');
    setIsNewUserAdmin(false);
    setSelectedTasks([]);
    setAddError('');
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddUser = async () => {
    if (!newUserEmail) {
      setAddError("L'adresse e-mail est requise.");
      return;
    }
    if (!activeCompany) {
      setAddError("Aucune entreprise n'est sélectionnée. Veuillez rafraîchir la page.");
      return;
    }

    try {
      const response = await axiosInstance.post('/company/addUser', {
        companyId: activeCompany.id,
        email: newUserEmail,
        isAdmin: isNewUserAdmin,
        defaultTaskIds: selectedTasks,
      });
      // La réponse de l'API contient maintenant directement l'utilisateur et son association CompanyUser
      const newUser = response.data;
      setUsers([...users, newUser]);
      handleCloseAddDialog();
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'USER_NOT_FOUND') {
        setAddError("Aucun utilisateur n'a été trouvé avec cette adresse e-mail.");
      } else if (errorCode === 'USER_ALREADY_IN_COMPANY') {
        setAddError("Cet utilisateur fait déjà partie de l'entreprise.");
      } else {
        console.error("Erreur inattendue lors de l'ajout de l'utilisateur:", err);
        setAddError("Une erreur est survenue.");
      }
    }
  };

  if (loading) {
    return <Typography>Chargement...</Typography>;
  }

  return (
    <Container>
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ mb: 2 }}
        >
          {notification.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Ajouter
        </Button>
        <TextField
          fullWidth
          variant="outlined"
          label="Rechercher par nom ou email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '1%', p: { xs: 1, md: 2 } }}>Statut</TableCell>
              <TableCell sx={{ p: { xs: 1, md: 2 } }}>
                <TableSortLabel
                  active={sortConfig.key === 'name'}
                  direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                  onClick={() => requestSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, md: 2 } }}>
                <TableSortLabel
                  active={sortConfig.key === 'email'}
                  direction={sortConfig.key === 'email' ? sortConfig.direction : 'asc'}
                  onClick={() => requestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, p: { xs: 1, md: 2 } }}>
                <TableSortLabel
                  active={sortConfig.key === 'phone'}
                  direction={sortConfig.key === 'phone' ? sortConfig.direction : 'asc'}
                  onClick={() => requestSort('phone')}
                >
                  Téléphone
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ p: { xs: 1, md: 2 } }}>Admin</TableCell>
              <TableCell align="right" sx={{ p: { xs: 1, md: 2 } }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.admins.map((user) => (
              <TableRow key={user.id} sx={(theme) => ({ backgroundColor: alpha(theme.palette.secondary.main, 0.08) })}>
                <TableCell align="center" sx={{ p: { xs: 1, md: 2 } }}>
                  {user.role === 'call' ? (
                    <WTooltip title="Renvoyer l'invitation">
                      <IconButton onClick={() => handleResendInvitation(user.id)} color="warning">
                        <RefreshIcon />
                      </IconButton>
                    </WTooltip>
                  ) : (
                    <WTooltip title="Compte actif">
                      <CheckCircleIcon color="success" />
                    </WTooltip>
                  )}
                </TableCell>
                <TableCell sx={{ p: { xs: 1, md: 2 } }}>
                  { (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (
                    <Typography component="em" sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      fontSize: '0.9rem',
                      textOverflow: 'ellipsis',
                      width: '10px',
                      minWidth: '100%'
                    }}>{user.email}</Typography>
                  ) }
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, md: 2 } }}>
                  <Typography component="em" sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      fontSize: '0.9rem',
                      textOverflow: 'ellipsis',
                      width: '130px',
                      minWidth: '100%'
                    }}>{user.email}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, p: { xs: 1, md: 2 } }}>{user.phone}</TableCell>
                <TableCell align="center" sx={{ p: { xs: 1, md: 2 } }}>
                  <WTooltip title="Changer le statut d'administrateur">
                    <Switch
                      checked={user.CompanyUser.isAdmin}
                      onChange={(e) => handleAdminChange(user, e.target.checked)}
                      disabled={user.id === currentUser.id} // On ne peut pas changer son propre rôle
                    />
                  </WTooltip>
                </TableCell>
                <TableCell align="right" sx={{ p: { xs: 1, md: 2 } }}>
                  {user.id === currentUser.id ? (
                    <IconButton disabled><DeleteIcon /></IconButton>
                  ) : (
                    <WTooltip title="Supprimer de l'entreprise">
                      <IconButton onClick={() => handleRemoveUser(user.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </WTooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sortedUsers.admins.length > 0 && sortedUsers.employees.length > 0 && (
              <TableRow sx={{ '& td': { border: 0 } }}>
                <TableCell colSpan={5} sx={{ py: 0.5, backgroundColor: 'grey.200' }}>
                  {/* This cell acts as a colored divider */}
                </TableCell>
              </TableRow>
            )}
            {sortedUsers.employees.map((user) => (
              <TableRow key={user.id}>
                <TableCell align="center" sx={{ p: { xs: 1, md: 2 } }}>
                  {user.role === 'call' ? (
                    <WTooltip title="Renvoyer l'invitation">
                      <IconButton onClick={() => handleResendInvitation(user.id)} color="warning">
                        <RefreshIcon />
                      </IconButton>
                    </WTooltip>
                  ) : (
                    <WTooltip title="Compte actif">
                      <CheckCircleIcon color="success" />
                    </WTooltip>
                  )}
                </TableCell>
                <TableCell sx={{ p: { xs: 1, md: 2 } }}>
                  { (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (
                    <Typography component="em" sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      fontSize: '0.9rem',
                      textOverflow: 'ellipsis',
                      width: '10px',
                      minWidth: '100%'
                    }}>{user.email}</Typography>
                  ) }
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, md: 2 } }}>
                  <Typography component="em" sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      fontSize: '0.9rem',
                      textOverflow: 'ellipsis',
                      width: '130px',
                      minWidth: '100%'
                    }}>{user.email}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, p: { xs: 1, md: 2 } }}>{user.phone}</TableCell>
                <TableCell align="center" sx={{ p: { xs: 1, md: 2 } }}>
                  <WTooltip title="Changer le statut d'administrateur">
                    <Switch
                      checked={user.CompanyUser.isAdmin}
                      onChange={(e) => handleAdminChange(user, e.target.checked)}
                      disabled={user.id === currentUser.id}
                    />
                  </WTooltip>
                </TableCell>
                <TableCell align="right" sx={{ p: { xs: 1, md: 2 } }}>
                  {user.id === currentUser.id ? (
                    <IconButton disabled><DeleteIcon /></IconButton>
                  ) : (
                    <WTooltip title="Supprimer de l'entreprise">
                      <IconButton onClick={() => handleRemoveUser(user.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </WTooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedUsers.revoked && sortedUsers.revoked.length > 0 && (
        <Accordion sx={{ mt: 2 }} expanded={revokedExpanded} onChange={() => setRevokedExpanded(!revokedExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="overline">Accès révoqués ({sortedUsers.revoked.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  {sortedUsers.revoked.map((user) => (
                    <TableRow key={user.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ width: '45%' }}>
                        <Typography color="text.secondary">
                          { (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : <em>{user.email}</em> }
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '45%' }}>
                        <Typography color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <WTooltip title="Réactiver l'accès">
                            <IconButton onClick={() => handleAdminChange(user, user.CompanyUser.isAdmin)}><RestoreIcon /></IconButton>
                          </WTooltip>
                          <WTooltip title="Supprimer définitivement">
                            <IconButton color="error" onClick={() => handlePermanentDeleteUser(user.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </WTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Ajouter un employé</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Saisissez l'adresse e-mail de l'utilisateur que vous souhaitez ajouter à l'entreprise.
          </DialogContentText>
          {addError && <Alert severity="error" sx={{ mt: 2 }}>{addError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label="Adresse Email"
            type="email"
            fullWidth
            variant="standard"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNewUserAdmin}
                  onChange={(e) => setIsNewUserAdmin(e.target.checked)}
                />
              }
              label="Donner les droits d'administrateur"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="tasks-select-label">Tâches par défaut</InputLabel>
              <Select
                labelId="tasks-select-label"
                multiple
                value={selectedTasks}
                onChange={(e) => setSelectedTasks(e.target.value)}
                input={<OutlinedInput label="Tâches par défaut" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const task = tasks.find(t => t.id === value);
                      return <Chip key={value} label={task ? task.name : ''} size="small" />;
                    })}
                  </Box>
                )}
              >
                {tasks.map((task) => (
                  <MenuItem key={task.id} value={task.id}>{task.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Annuler</Button>
          <Button onClick={handleAddUser}>Ajouter</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Employees;
